/**
 * Type-safe identifiers for all registered frontend UI actions.
 *
 * Use ActionIds constants instead of string literals when calling
 * actionRegistry.execute() or actionRegistry.get() to prevent typos
 * and enable IDE auto-completion.
 */
export const ActionIds = {
    SIDEBAR_TOGGLE_TODO: 'sidebar.toggle.todo',
    SIDEBAR_TOGGLE_KNOWLEDGE: 'sidebar.toggle.knowledge',
    TIMELINE_JUMP_TO_TODAY: 'timeline.jumpToToday',
    TIMELINE_JUMP_TO_DATE: 'timeline.jumpToDate',
    TIMELINE_SET_CELL_WIDTH_VIEW: 'timeline.setCellWidthView',
    BOARD_TOGGLE_SORT: 'board.toggleSort',
    FILTERS_CLEAR: 'filters.clear',
    RESOURCE_FILTERS_CLEAR: 'resourceFilters.clear',
    DIALOG_NEW_ISSUE: 'dialog.newIssue',
    DIALOG_NEW_PUBLIC_HOLIDAY: 'dialog.newPublicHoliday',
    DIALOG_NEW_VACATION: 'dialog.newVacation',
    DIALOG_NEW_PROJECT: 'dialog.newProject',
    DIALOG_NEW_DAILY: 'dialog.newDaily',
    DIALOG_NEW_EMPLOYEE: 'dialog.newEmployee',
    DIALOG_NEW_KEYWORD: 'dialog.newKeyword',
    DIALOG_NEW_ISSUE_TYPE: 'dialog.newIssueType',
    DIALOG_NEW_ISSUE_STATUS: 'dialog.newIssueStatus',
    EXPORT_ICAL_CURRENT_YEAR: 'export.icalCurrentYear',
    AUTH_LOGOUT: 'auth.logout',
} as const;

/** Union type of all valid action IDs derived from ActionIds constant object. */
export type ActionId = typeof ActionIds[keyof typeof ActionIds];

/**
 * Custom window event name dispatched when the "Focus on Today" action fires.
 *
 * Views that need to react (e.g. Y-axis scroll in BoardsView) listen for this
 * event on `window` via RxJS `fromEvent` with `takeUntil(disconnected$)`.
 */
export const FOCUS_TODAY_EVENT = 'timeline-focus-today' as const;

/**
 * Categorizes actions by their purpose.
 *
 * @experimental May be restructured in future PRDs when Command Palette is added.
 */
export type ActionCategory = 'navigation' | 'create' | 'view' | 'export' | 'system';

/**
 * Defines the contract for a registerable frontend UI action.
 *
 * Actions are UI-orchestration units: they connect UI triggers (buttons,
 * shortcuts, menus) to application effects (state changes, dialogs, backend calls).
 * They are distinct from @o2d/commands which handle atomic backend mutations.
 *
 * @template TPayload - Optional payload type for parameterized actions (default: void).
 */
export interface ActionDefinition<TPayload = void> {
    /** Unique action identifier. Must match an ActionId value. */
    readonly id: ActionId;

    /** Human-readable label for display in menus or tooltips. */
    readonly label: string;

    /** Optional icon identifier for visual representation. */
    readonly icon?: string;

    /**
     * Executes the action with an optional payload.
     *
     * @param payload - Optional data passed to parameterized actions (e.g., DateTime for jumpToDate).
     */
    readonly execute: (payload: TPayload) => void | Promise<void>;

    /**
     * Returns whether the action can currently be executed.
     *
     * Used for privilege checks (reads appState.privileges synchronously).
     * If undefined, the action is always considered enabled.
     *
     * @returns true if the action may be executed, false otherwise.
     */
    readonly canExecute?: () => boolean;

    /**
     * Optional keyboard shortcut for this action.
     *
     * Format: "Modifier+Key" (Modifier PascalCase, + separator, Key uppercase).
     * Modifier order: Ctrl, Shift, Alt.
     * Examples: "Ctrl+B", "Ctrl+Shift+F", "Ctrl+T".
     *
     * Values are assigned by the KeyboardShortcutController PRD.
     * All current actions leave this field undefined.
     */
    readonly keybinding?: string;

    /**
     * Optional category for grouping actions (e.g., in Command Palette).
     *
     * @experimental May be restructured in future PRDs.
     */
    readonly category?: ActionCategory;
}
